<template>
  <div id="AccountConversionDefinitions" class="Main-Content">
    <ValidationObserver v-slot="{ invalid }" slim>
      <LoadingLayout v-if="loading" :page="true" />
      
      <Page
        v-if="!loading"
        :title="$t('Conversion Definitions')"
        :primaryAction="{
          label: $t('Add'),
          onAction: addDefinition,
          disabled: !(sitesWithTerms && sitesWithTerms.length)
        }"
        :secondaryActions="[{
          label: $t('Notes'),
          onAction: () => eventHub.$emit('showAccountNotes', 'CONVERSIONDEFINITION'),
          disabled: !(sitesWithTerms && sitesWithTerms.length)
        }]"
        size="fullWidth"
      >

        <Sheet
          :title="$t('ConversionDefinitions.changeChargeCalculationRule')"
          :width="700"
          :visible="changingCalculationRule"
          @close="changingCalculationRule = false"
          padded
        >
          <template v-slot:actions>
            <Button type="primary" :disabled="saving" @click="updateCalculationRule">{{$t('Update')}}</Button>
          </template>
          <Card>
            <CardSection>
              <FormLayout>
                <Select
                  :label="$t('ConversionDefinitions.chargeCalculationRule')"
                  :options="calculationRules"
                  v-model="calculationRuleEditValue"
                />
              </FormLayout>
            </CardSection>
          </Card>
        </Sheet>

        <Sheet
          :title="isEdit ? $t('ConversionDefinitions.editConversionDefinition') : $t('ConversionDefinitions.addConversionDefinition')"
          :width="700"
          :visible="editingDefinition"
          @close="cancelEditDefinition"
          padded
        >
          <template v-slot:actions>
            <Button 
              v-if="defToEdit.id" 
              type="primary" 
              :disabled="disableSave(invalid) || saving" 
              @click="updateDef"
            >{{$t('Update')}}</Button>
            <Button 
              v-if="!defToEdit.id" 
              type="primary" 
              :disabled="(showValidationUI && disableSave(invalid)) || saving"
              @click="addDef(invalid)"
            >{{$t('Add')}}</Button>
          </template>
          <Card>
            <CardSection>

              <FormLayout>
                <Select
                  :readonly="isEdit"
                  :label="$t('ConversionDefinitions.forDomain')"
                  v-model="defToEdit.siteId"
                  :options="domainOptions"
                  :placeholder="$t('ConversionDefinitions.chooseADomain')"
                  :helpText="$t('ConversionDefinitions.whichDomainHelp')"
                />
                <validation-provider :name="$t('Name')" rules="required" immediate v-slot="{ errors }">
                  <TextField
                    :label="$t('Name')"
                    v-model.trim="defToEdit.name"
                    :helpText="$t('ConversionDefinitions.nameHelp')"
                    :error="showValidationUI ? (errors[0] || nameExists) : ''"
                  />
                </validation-provider>

                <!-- <validation-provider :name="$t('Category')" rules="required" v-slot="{ errors }"> -->
                  <Select
                    :readonly="isEdit && defToEdit.originalCategory"
                    :label="$t('ConversionDefinitions.Category')"
                    v-model="defToEdit.category"
                    :options="categories"
                    :placeholder="$t('ConversionDefinitions.chooseACategory')"
                    :helpText="$t('ConversionDefinitions.categoryHelp')"
                    :error="showValidationUI ? categoryError : ''"
                  />
                <!-- </validation-provider> -->

                <FormLayoutGroup v-if="!isEdit || defToEdit.action1Type">
                  <Select
                    :readonly="isEdit"
                    :label="$t('ConversionDefinitions.ruleX', { value: 1 } )"
                    v-model="defToEdit.action1Type"
                    :options="definitionRules"
                    :error="showValidationUI ? missingRulesError : ''"
                  />
                  <validation-provider 
                    v-if="hasReference(defToEdit.action1Type)" 
                    :name="$t('ConversionDefinitions.ruleXReference', { value: 1 } )" 
                    immediate
                    :rules="requiresReference(defToEdit.action1Type) ? 'required' : ''" 
                    v-slot="{ errors }"
                  >
                    <TextField
                      :readonly="isEdit"
                      v-if="hasReference(defToEdit.action1Type)"
                      :label="$t('ConversionDefinitions.ruleXReference', { value: 1 } )"
                      v-model.trim="defToEdit.action1Reference"
                      :placeholder="requiresReference(defToEdit.action1Type) ? $t('ConversionDefinitions.Required') : $t('ConversionDefinitions.Any')"
                      :helpText="help(defToEdit.action1Type)"
                      :error="requiresReference(defToEdit.action1Type) && showValidationUI ? errors[0] : ''"
                    />
                  </validation-provider>
                </FormLayoutGroup>

                <FormLayoutGroup v-if="!isEdit || defToEdit.action2Type">
                  <Select
                    :readonly="isEdit"
                    :label="$t('ConversionDefinitions.ruleX', { value: 2 } )"
                    v-model="defToEdit.action2Type"
                    :options="definitionRules"
                  />
                  <validation-provider 
                    v-if="hasReference(defToEdit.action2Type)" 
                    :name="$t('ConversionDefinitions.ruleXReference', { value: 2 } )" 
                    immediate
                    :rules="requiresReference(defToEdit.action2Type) ? 'required' : ''" 
                    v-slot="{ errors }"
                  >
                    <TextField
                      :readonly="isEdit"
                      v-if="hasReference(defToEdit.action2Type)"
                      :label="$t('ConversionDefinitions.ruleXReference', { value: 2 } )"
                      v-model.trim="defToEdit.action2Reference"
                      :placeholder="requiresReference(defToEdit.action2Type) ? 'Required' : 'Any'"
                      :helpText="help(defToEdit.action2Type)"
                      :error="requiresReference(defToEdit.action2Type) && showValidationUI ? errors[0] : ''"
                    />
                  </validation-provider>
                </FormLayoutGroup>

                <FormLayoutGroup v-if="!isEdit || defToEdit.action3Type">
                  <Select
                    :readonly="isEdit"
                    :label="$t('ConversionDefinitions.ruleX', { value: 3 } )"
                    v-model="defToEdit.action3Type"
                    :options="definitionRules"
                  />
                  <validation-provider 
                    v-if="hasReference(defToEdit.action3Type)" 
                    :name="$t('ConversionDefinitions.ruleXReference', { value: 3 } )" 
                    immediate
                    :rules="requiresReference(defToEdit.action3Type) ? 'required' : ''"  
                    v-slot="{ errors }"
                  >
                    <TextField
                      :readonly="isEdit"
                      v-if="hasReference(defToEdit.action3Type)"
                      :label="$t('ConversionDefinitions.ruleXReference', { value: 3 } )"
                      v-model.trim="defToEdit.action3Reference"
                      :placeholder="requiresReference(defToEdit.action3Type) ? $t('ConversionDefinitions.Required') : $t('ConversionDefinitions.Any')"
                      :helpText="help(defToEdit.action3Type)"
                      :error="requiresReference(defToEdit.action3Type) && showValidationUI ? errors[0] : ''"
                    />
                  </validation-provider>
                </FormLayoutGroup>

                <FormLayoutGroup>
                  <Select
                    :disabled="!theUser.isSuper"
                    :label="$t('ConversionDefinitions.isFanplayrAttribution')"
                    v-model="defToEdit.isFPAttribution"
                    :options="[{
                      value: 1,
                      label: $t('Yes')
                    }, {
                      value: 0,
                      label: $t('No')
                    }]"
                  />
                </FormLayoutGroup>

                <FormLayoutGroup v-show="defToEdit.isFPAttribution">
                  <Select
                    :disabled="!theUser.isSuper"
                    :label="$t('ConversionDefinitions.chargeType')"
                    v-model="defToEdit.chargeType"
                    :options="[{
                      value: 'P',
                      label: $t('ConversionDefinitions.Percent')
                    }, {
                      value: 'C',
                      label: $t('ConversionDefinitions.Fixed')
                    }]"
                  />
                  <TextField
                    :disabled="!theUser.isSuper"
                    :label="$t('ConversionDefinitions.chargeRate')"
                    v-model.trim="defToEdit.chargeRate"
                    type="number"
                    :error="showValidationUI ? chargeRateError : ''"
                  >
                    <template #prefix>{{ defToEdit.chargeType === 'P' ? '%' : getCurrencySymbol(theUser.accountCurrency) }}</template>
                  </TextField>
                </FormLayoutGroup>

                <FormLayoutGroup v-show="defToEdit.isFPAttribution">
                  <TextField
                    :disabled="!theUser.isSuper"
                    v-show="defToEdit.chargeType === 'P'"
                    :label="$t('ConversionDefinitions.minimumCharge')"
                    type="number"
                    :error="showValidationUI ? minChargeError : ''"
                    v-model.trim="defToEdit.minCharge"
                  >
                    <template #prefix>{{ getCurrencySymbol(theUser.accountCurrency) }}</template>
                  </TextField>
                  <TextField
                    :disabled="!theUser.isSuper"
                    v-show="defToEdit.chargeType === 'P'"
                    :label="$t('ConversionDefinitions.maximumCharge')"
                    type="number"
                    :error="showValidationUI ? maxChargeError : ''"
                    v-model.trim="defToEdit.maxCharge"
                  >
                    <template #prefix>{{ getCurrencySymbol(theUser.accountCurrency) }}</template>
                  </TextField>
                </FormLayoutGroup>
              </FormLayout>

            </CardSection>
          </Card>
        </Sheet>

        <Layout v-if="sitesWithTerms && sitesWithTerms.length">
          <LayoutSection>
            <Card>
              <CardSection>
                <Select
                  :label="$t('ConversionDefinitions.forDomain')"
                  :options="domainFilterOptions"
                  v-model="filterDomain"
                />
              </CardSection>

              <CardSection>
                <template v-if="filterDomain > -1">
                  <div>
                    <span v-html="$t('ConversionDefinitions.chargeCalculationRuleMessage', { value: currentCalculationRule.label })"></span>
                    <span v-if="!hasMixedDefinitionRules">&mdash;</span>
                    <span v-if="theUser.isSuper && !hasMixedDefinitionRules" @click="editCalculationRule" class="link">{{$t('ConversionDefinitions.Change')}}</span>
                  </div>
                  <div class="help">{{ currentCalculationRule.help }}</div>
                </template>
                <template v-else>
                  {{$t('ConversionDefinitions.domainChargeCalculationMessage')}}
                </template>
              </CardSection>

              <CardSection>
          
                <Tabs
                  :tabs="tabs"
                  v-model="filterStatus"
                />

                <DataTable
                  :columns="columns"
                  :rows="sortedDefinitions"
                  :sort.sync="sortBy"
                  :row-actions="rowActions"
                  :loading="loading"
                  compact
                >
                  <template v-slot:col:name="{row}">
                    <div v-if="!row.name" @click="editDefinition(row)" class="editLink"><span style="color: #ce3017;">&lt;{{$t('None')}}&gt;</span></div>
                    <div v-else @click="editDefinition(row)" class="editLink">{{ row.name }}</div>
                    <Caption>#{{row.id}}</Caption>
                  </template>
                  <template v-slot:col:siteId="{row}">
                    <Badge v-if="!row.siteId">{{$t('ConversionDefinitions.Base')}}</Badge>
                    <span v-else-if="getDomainFromId(row.siteId)">{{ getDomainFromId(row.siteId) }}</span>
                    <Badge v-else status="warning">{{$t('ConversionDefinitions.Error')}}</Badge>
                  </template>
                  <template v-slot:col:category="{value, row}">
                    <Badge v-if="!row.category" status="warning">{{$t('ConversionDefinitions.notSet')}}</Badge>
                    <span v-else>{{ getCategoryName(value) }}</span>
                  </template>
                  <template v-slot:col:action1Type="{row}">
                    {{ getActionType(row.action1Type) }}
                  </template>
                  <template v-slot:col:action1Reference="{row, value}">
                    <Badge v-if="!row.action1Reference && hasReference(row.action1Type) && !requiresReference(row.action1Type)">{{$t('ConversionDefinitions.Any')}}</Badge>
                    <Badge v-else-if="!row.action1Reference && hasReference(row.action1Type) && requiresReference(row.action1Type)" status="warning">{{$t('ConversionDefinitions.Missing')}}</Badge>
                    <span v-else>{{ value }}</span>
                  </template>
                  <template v-slot:col:action2Type="{row}">
                    {{ getActionType(row.action2Type) }}
                  </template>
                  <template v-slot:col:action2Reference="{row, value}">
                    <Badge v-if="!row.action2Reference && hasReference(row.action2Type) && !requiresReference(row.action2Type)">{{$t('ConversionDefinitions.Any')}}</Badge>
                    <Badge v-else-if="!row.action2Reference && hasReference(row.action2Type) && requiresReference(row.action2Type)" status="warning">{{$t('ConversionDefinitions.Missing')}}</Badge>
                    <span v-else>{{ value }}</span>
                  </template>
                  <template v-slot:col:action3Type="{row}">
                    {{ getActionType(row.action3Type) }}
                  </template>
                  <template v-slot:col:action3Reference="{row, value}">
                    <Badge v-if="!row.action3Reference && hasReference(row.action3Type) && !requiresReference(row.action3Type)">{{$t('ConversionDefinitions.Any')}}</Badge>
                    <Badge v-else-if="!row.action3Reference && hasReference(row.action3Type) && requiresReference(row.action3Type)" status="warning">{{$t('ConversionDefinitions.Missing')}}</Badge>
                    <span v-else>{{ value }}</span>
                  </template>
                  <template v-slot:col:chargeType="{value, row}">
                    <span v-if="!row.isFPAttribution">—</span>
                    <span v-else>{{ getChargeType(value) }}</span>
                  </template>
                  <template v-slot:col:chargeRate="{row}">
                    <span v-if="!row.isFPAttribution">—</span>
                    <span v-else-if="row.chargeType === 'P'"><Formatter type="percent" :value="row.chargeRate / 100"/></span>
                    <span v-else><Formatter type="currency" :value="row.chargeRate"/></span>
                  </template>
                  <template v-slot:col:minCharge="{value, row}">
                    <span v-if="!row.isFPAttribution || row.chargeType === 'C'">—</span>
                    <span v-else>{{ value }}</span>
                  </template>
                  <template v-slot:col:maxCharge="{value, row}">
                    <span v-if="!row.isFPAttribution || row.chargeType === 'C'">—</span>
                    <span v-else>{{ value }}</span>
                  </template>
                  <template v-slot:col:isFPAttribution="{value}">
                    <Badge
                      :status="value ? 'success' : 'default'"
                    >{{value ? $t('Yes') : $t('No') }}</Badge>
                  </template>
                  <template v-slot:col:createdBy="{row, value}">
                    <span v-if="!value || value.indexOf('@') > -1">{{ value }}</span>
                    <span v-else>{{ row.createdByName }}</span>
                  </template>
                  <template v-slot:col:updatedBy="{row, value}">
                    <span v-if="!value || value.indexOf('@') > -1">{{ value }}</span>
                    <span v-else>{{ row.updatedByName }}</span>
                  </template>
                </DataTable>

              </CardSection>
            </Card>
          </LayoutSection>
        </Layout>
        <Layout v-else>
          <LayoutSection>
            <Card>
              <CardSection>
                <div class="ErrorText">
                  <Icon icon="fas fas fa-exclamation-triangle fa-2x" />
                  {{$t('AccountConversionDefinitions.commercialTermsError')}}
                </div>
              </CardSection>
            </Card>
          </LayoutSection>
        </Layout>

      </Page>
    </ValidationObserver>
  </div>
</template>

<script>

import _ from 'lodash'
// import { extend as ValidateExtend } from 'vee-validate/dist/vee-validate.full'

export default {
  name: 'AccountConversionDefinitions',
  data () {
    return {
      definitions: [],
      categories: [],
      definitionRules: [],
      sitesWithTerms: [],

      filterStatus: 'active',
      filterDomain: 0,

      sortBy: null,
      editingDefinition: false,
      defToEdit: {},
      defToEditCopy: {},
      showValidationUI: false,
      isEdit: false,
      loading: true,
      saving: false,
      changingCalculationRule: false,
      calculationRuleEditValue: null
    }
  },
  mounted() {
    if (!this.theUser.hasConversionDefinitionAccess) {
      this.$router.replace('/account/conversions/')
      return
    }

    NProgress.start()
    Promise.all([
      this.api.getConversionDefinitionRules(),
      this.api.getConversionDefinitionCategories(),
      this.api.getConversionDefinitions(),
      this.api.getSitesWithTerms()
    ])
      .then((response) => {
        let [ definitionRules, categories, definitions, sitesWithTerms ] = response 

        this.definitionRules = definitionRules.map(dr => {
          return {
            ...dr,
            label: this.$t(dr.label),
            help: this.$t(dr.help)
          }
        })
        this.categories = categories.map(c => {
          return {
            value: c.id,
            label: this.$t(c.name)
          }
        })
        this.definitions = definitions
        this.sitesWithTerms = sitesWithTerms

        this.sortBy = 'name'

        // when switching check that a calculation rule is set
        // if it's not then set to all
        // if it's wrong, update, and let the user know it has been updated
        this.$watch('currentTerm', (newTerm) => {
          // for all
          if (!newTerm) return
          if (!newTerm.componentCalculationRule) {
            // update DB with all
            this.api.updateConversionCalculationRuleToAll(newTerm.siteId)
            .then((updated) => {
              if (updated) {
                newTerm.componentCalculationRule = 'ALL'
              } else {
                this.$ui.alert({
                  title: this.$t('ConversionDefinitions.updatingChargeCalculationRuleError')
                })
              }
            })
            .catch(this.apiCatch)
          } else {
            // check for not all and mixed
            this.checkForMixedAndUpdate()
          }
        }, { immediate: true })

        this.$watch('hasMixedDefinitionRules', () => {
          this.checkForMixedAndUpdate()
        })

        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
  },
  computed: {
    calculationRules() {
      return [
        {
          value: 'HIGHEST',
          label: this.$t('ConversionDefinitions.Highest'),
          help: this.$t('ConversionDefinitions.highestRuleHelp')
        },
        {
          value: 'LOWEST',
          label: this.$t('ConversionDefinitions.Lowest'),
          help: this.$t('ConversionDefinitions.lowestRuleHelp')
        },
        {
          value: 'ALL',
          label: this.$t('All'),
          help: this.$t('ConversionDefinitions.allRulesHelp')
        }
      ]
    },
    chargeRateError() {
      if (this.defToEdit && this.theUser.isSuper) {
        if (this.defToEdit.chargeType === 'P' && (this.defToEdit.chargeRate < 0 || this.defToEdit.chargeRate > 100)) {
          return this.$t('ConversionDefinitions.chargeRateRangeError')
        } else if (this.defToEdit.chargeRate < 0) {
          return this.$t('ConversionDefinitions.chargeRateAboveZeroError')
        }
      }
      return ''
    },
    minChargeError() {
      if (this.defToEdit && this.theUser.isSuper && this.defToEdit.chargeType === 'P') {
        if (this.defToEdit.maxCharge && this.defToEdit.minCharge >= this.defToEdit.maxCharge) {
          return this.$t('ConversionDefinitions.minChargeError')
        }
      }
      return ''
    },
    categoryError() {
      if (this.defToEdit && this.theUser.isSuper && !this.defToEdit.category) {
        return this.$t('ConversionDefinitions.categoryError')
      }
      return ''
    },
    maxChargeError() {
      // if (this.defToEdit && this.theUser.isSuper && this.defToEdit.chargeType === 'P') {
      //   if (this.defToEdit.minCharge !== 0 && this.defToEdit.minCharge >= this.defToEdit.maxCharge) {
      //     return 'The maximum charge must be greater than the minimum charge'
      //   }
      // }
      return ''
    },
    nameExists() {
      if (this.defToEdit && this.theUser.isSuper && this.defToEdit.name && this.defToEdit.name.trim()) {
        if (this.definitions.find(d => ((d.name && d.name.trim().toLowerCase()) === this.defToEdit.name.trim().toLowerCase()) 
          && (d.id !== this.defToEdit.id))) {
          return this.$t('ConversionDefinitions.nameExistsError')
        }
      }
      return ''
    },
    missingRulesError() {
      if (!this.defToEdit.action1Type && !this.defToEdit.action2Type && !this.defToEdit.action3Type) {
        return this.$t('ConversionDefinitions.missingRulesError')
      }
      return ''
    },
    // for current domain
    hasMixedDefinitionRules() {
      // SESSION, ORDER, MIXED
      const actionTypes = ['action1Type', 'action2Type', 'action3Type']
      let defs = []
      for (let def of this.filteredByDomainDefinitions
        .filter(d => d.status === 'Active' && d.isFPAttribution)
      ) {
        let type = ''
        for (let actionType of actionTypes) {
          if (def[actionType]) {
            if (def[actionType].indexOf('ORDER') > -1) {
              if (!type || type === 'ORDER') {
                type = 'ORDER'
              } else if (type !== 'ORDER') {
                type = 'MIXED'
              } else {
                type = 'SESSION'
              }
            } else {
              if (type && type !== 'SESSION') {
                type = 'MIXED'
              } else {
                type = 'SESSION'
              }
            }
          }
        }
        defs.push(type)
        type = ''
      }
      return [...new Set(defs)].length > 1
    },
    currentTerm() {
      return this.sitesWithTerms.find(s => s.siteId === this.filterDomain)
    },
    currentCalculationRule() {
      if (this.currentTerm && this.currentTerm.componentCalculationRule) {
        return this.calculationRules.find(c => c.value === this.currentTerm.componentCalculationRule)
      }
      return this.calculationRules.find(c => c.value === 'ALL')
    },
    filteredByDomainDefinitions() {
      let defs = this.definitions

      // base
      if (this.filterDomain === 0) {
        defs = defs.filter(d => !d.siteId)
      // by domain
      } else if (this.filterDomain > -1) {
        defs = defs.filter(d => d.siteId === this.filterDomain)
      // all (0)
      }

      return defs
    },
    filteredByCurrentStatus() {
      let defs = this.filteredByDomainDefinitions

      if (this.filterStatus === 'active') {
        defs = defs.filter(d => d.status === 'Active')
      } else if (this.filterStatus === 'inactive') {
        defs = defs.filter(d => d.status === 'Inactive')
      }

      return defs
    },
    sortedDefinitions() {
      const {filteredByCurrentStatus, sortBy} = this
      if (sortBy) {
        return _.orderBy(filteredByCurrentStatus, [sortBy.column], [sortBy.direction])
      }
      return filteredByCurrentStatus
    },
    tabs() {
      return [
        { id: 'active', label: this.$t('Active'), badge: this.numActiveDefinitions || '0' },
        { id: 'inactive', label: this.$t('Inactive'), badge: this.numInactiveDefinitions || '0' },
        { id: 'all', label: this.$t('All'), badge: this.filteredByDomainDefinitions.length || '0' }
      ]
    },
    numActiveDefinitions() {
      return this.filteredByDomainDefinitions.filter(d => d.status === 'Active').length
    },
    numInactiveDefinitions() {
      return this.filteredByDomainDefinitions.filter(d => d.status === 'Inactive').length
    },
    domainOptions() {
      const domainsOpt = this.sitesWithTerms.map(s => {
        return {
          // label: `${s.domain} (${this.definitions.filter(d => d.siteId === s.siteId).length})`,
          label: `${s.domain}`,
          value: s.siteId
        }
      })
      
      // set base desc
      if (domainsOpt.length) {
        domainsOpt[0].description = this.$t('ConversionDefinitions.baseDomainDesc')
      }

      return domainsOpt
    },
    domainFilterOptions() {
      const domainsOpt = this.domainOptions.concat([])
      domainsOpt.push({
        // label: `All Domains (${this.definitions.length})`,
        label: this.$t('ConversionDefinitions.allDomains'),
        value: -1
      })
      return domainsOpt
    },
    columns() {
      return [
        { key: 'name', label: this.$t('Name'), sortable: true, fixed: true },
        { key: 'siteId', label: this.$t('Domain'), sortable: true },
        { key: 'category', label: this.$t('ConversionDefinitions.Category'), sortable: true },
        { key: 'isFPAttribution', label: this.$t('ConversionDefinitions.isFPAttribution'), sortable: true },
        { key: 'action1Type', label: this.$t('ConversionDefinitions.ruleX', { value: 1 }), sortable: true },
        { key: 'action1Reference', label: this.$t('ConversionDefinitions.ruleXRef', { value: 1 }), sortable: true },
        { key: 'action2Type', label: this.$t('ConversionDefinitions.ruleX', { value: 2 }), sortable: true },
        { key: 'action2Reference', label: this.$t('ConversionDefinitions.ruleXRef', { value: 2 }), sortable: true },
        { key: 'action3Type', label: this.$t('ConversionDefinitions.ruleX', { value: 3 }), sortable: true },
        { key: 'action3Reference', label: this.$t('ConversionDefinitions.ruleXRef', { value: 3 }), sortable: true },
        { key: 'chargeType', label: this.$t('ConversionDefinitions.chargeType'), sortable: true },
        { key: 'chargeRate', label: this.$t('ConversionDefinitions.chargeRate'), sortable: true },
        { key: 'minCharge', label: this.$t('ConversionDefinitions.minimumCharge'), format: 'currency', sortable: true },
        { key: 'maxCharge', label: this.$t('ConversionDefinitions.maximumCharge'), format: 'currency', sortable: true },
        { key: 'status', label: this.$t('Status'), sortable: true },
        { key: 'createdBy', label: this.$t('Created By'), sortable: true },
        { key: 'createdOn', label: this.$t('Created On'), format: 'dateShort', sortable: true },
        { key: 'updatedBy', label: this.$t('Updated By'), sortable: true },
        { key: 'updatedOn', label: this.$t('Updated On'), format: 'dateShort', sortable: true },
      ]
    },
    rowActions() {
      return row => {
        let disabled = false
        if ((!this.theUser.isSuper && row.isFPAttribution) || this.saving) {
          disabled = true
        }
        if (row.status === 'Active') {
          return [{
            disabled,
            label: this.$t('Disable'),
            onAction: (row) => {
              this.disableDef(row)
            }
          }]
        } else {
          return [{
            disabled,
            label: this.$t('Enable'),
            onAction: (row) => {
              this.enableDef(row)
            }
          }]
        }
      }
    },
  },
  methods: {
    checkForMixedAndUpdate() {
      // now mixed
      if (this.hasMixedDefinitionRules && this.currentCalculationRule.value !== 'ALL') {
        this.api.updateConversionCalculationRuleToAll(this.currentTerm.siteId)
        .then((updated) => {
          if (updated) {
            this.currentTerm.componentCalculationRule = 'ALL'
            this.$ui.alert({
              title: this.$t('ConversionDefinitions.chargeCalculationRuleChanged'),
              content: this.$t('ConversionDefinitions.chargeCalculationRuleChangedMessage')
            })
          } else {
            this.$ui.alert({
              title: this.$t('ConversionDefinitions.updatingChargeCalculationRuleError')
            })
          }
        })
        .catch(this.apiCatch)
      }
    },
    getDomainFromId(id) {
      let site = this.sitesWithTerms.find(s => s.siteId === id)
      if (site) {
        return site.domain
      } else {
        return null
      }
    },
    disableSave(invalid) {
      return invalid 
        || !!this.chargeRateError 
        || !!this.minChargeError 
        || !!this.maxChargeError 
        || !!this.missingRulesError 
        || !!this.categoryError
        || !!this.nameExists
    },
    getCurrencySymbol(iso) {
      return iso
    },
    getCategoryName(id) {
      const cat = this.categories.find(c => c.value === id)
      if (cat) return cat.label
      return ''
    },
    getDefinitionType(type) {
      return this.definitionRules.find(d => d.value === type)
    },
    getActionType(type) {
      const def = this.getDefinitionType(type)
      if (def) return def.label
      return ''
    },
    getChargeType(type) {
      if (type === 'P') {
        return this.$t('ConversionDefinitions.Percent')
      }
      if (type === 'C') {
        return this.$t('ConversionDefinitions.Fixed')
      }
      return ''
    },
    help(type) {
      let def = this.getDefinitionType(type)
      if (def) {
        return def.help
      }
      return ''
    },
    hasReference(type) {
      let def = this.getDefinitionType(type)
      if (def) {
        return !!def.hasReference
      }
      return false
    },
    requiresReference(type) {
      let def = this.getDefinitionType(type)
      if (def) {
        return !!def.requiresReference
      }
      return false
    },
    addDefinition() {
      this.editingDefinition = true
      this.isEdit = false

      this.defToEdit = {
        action1Reference: '',
        action1Type: '',
        action2Reference: '',
        action2Type: '',
        action3Reference: '',
        action3Type: '',
        category: null,
        chargeRate: 0,
        chargeType: 'P',
        isFPAttribution: this.theUser.isSuper ? 1 : 0,
        maxCharge: 0,
        minCharge: 0,
        name: '',
        siteId: this.filterDomain > -1 ? this.filterDomain : 0
      }
      this.defToEditCopy = _.cloneDeep(this.defToEdit)
    },
    editDefinition(def) {
      this.editingDefinition = true
      this.isEdit = true
      this.defToEdit = Object.assign({
        originalCategory: def.category
      }, def)
      this.defToEditCopy = _.cloneDeep(this.defToEdit)
      this.showValidationUI = true
    },
    async cancelEditDefinition() {
      if (!_.isEqual(this.defToEdit, this.defToEditCopy)) {
        const confirm = await this.$ui.confirm({
          title: this.$t('Unsaved Changes'),
          content: this.$t('ConversionDefinitions.unsavedChangesMessage'),
          primaryActionLabel: this.$t('Lose changes and return to list'),
          primaryActionType: 'destructive',
          secondaryActionLabel: this.$t('Continue editing')
        })
        if (!confirm) {
          return
        }
      }
      this.clearEditData()
    },
    clearEditData() {
      this.editingDefinition = false
      this.showValidationUI = false
      this.defToEdit = {}
      this.defToEditCopy = {}
    },
    enableDef(def) {
      NProgress.start()
      this.saving = true
      this.api.setConversionDefinitionStatus(def.id, 'Active')
      .then((updatedDef) => {
        const index = this.definitions.findIndex(d => d.id === updatedDef.id)
        this.definitions.splice(index, 1)
        this.definitions.unshift(updatedDef)

        this.$ui.toast({ title: this.$t('Enabled') })
        this.clearEditData()
      })
      .catch(this.apiCatch)
      .finally(() => {
        this.saving = false
        NProgress.done()
      })
    },
    disableDef(def) {
      NProgress.start()
      this.saving = true
      this.api.setConversionDefinitionStatus(def.id, 'Inactive')
      .then((updatedDef) => {
        const index = this.definitions.findIndex(d => d.id === updatedDef.id)
        this.definitions.splice(index, 1)
        this.definitions.unshift(updatedDef)

        this.$ui.toast({ title: this.$t('Disabled') })
        this.clearEditData()
      })
      .catch(this.apiCatch)
      .finally(() => {
        this.saving = false
        NProgress.done()
      })
    },
    updateDef() {
      NProgress.start()
      this.saving = true
      this.api.updateConversionDefinition(this.defToEdit)
      .then((updatedDef) => {
        const index = this.definitions.findIndex(d => d.id === updatedDef.id)
        this.definitions.splice(index, 1)
        this.definitions.unshift(updatedDef)

        this.$ui.toast({ title: this.$t('Updated') })
        this.clearEditData()
      })
      .catch(this.apiCatch)
      .finally(() => {
        this.saving = false
        NProgress.done()
      })
    },
    addDef(invalid) {
      if (this.disableSave(invalid)) {
        this.showValidationUI = true
        return
      }
      NProgress.start()
      this.saving = true
      this.api.addConversionDefinition(this.defToEdit)
      .then((addedDef) => {
        // if not showing all show domain from def
        if (this.filterDomain !== -1) {
          this.filterDomain = addedDef.siteId || 0
        }
        this.definitions.unshift(addedDef)
        this.$ui.toast({ title: this.$t('Added') })
        this.clearEditData()
      })
      .catch(this.apiCatch)
      .finally(() => {
        this.saving = false
        NProgress.done()
      })
    },
    editCalculationRule() {
      this.calculationRuleEditValue = this.currentCalculationRule.value
      this.changingCalculationRule = true
    },
    async updateCalculationRule() {
      NProgress.start()
      this.saving = true
      return this.api.updateConversionCalculationRule(this.currentTerm.siteId, this.calculationRuleEditValue)
      .then((updated) => {
        if (updated) {
          this.currentTerm.componentCalculationRule = this.calculationRuleEditValue
          this.changingCalculationRule = false
          return true
        } else {
          this.$ui.alert({
            title: this.$t('ConversionDefinitions.updateRuleError', { value: this.calculationRuleEditValue})
          })
          return false
        }
      })
      .catch(this.apiCatch)
      .finally(() => {
        this.saving = false
        NProgress.done()
      })
    }
  }
}

</script>

<style lang="scss">
  .editLink {
    cursor: pointer;
    color: #006fbb;
    white-space: nowrap;
  }
  .help {
    margin-top: 4px;
    color: #637381;
    font-size: 14px;
    line-height: 20px;
  }
</style>